<template>
  <ValidationObserver ref="observer" v-slot="{ invalid }">
    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h3 class="card-title">{{ title }}</h3>

        <div class="d-flex gap-2">

          <!-- ANNULER -->
          <button
              v-if="!viewMode"
              type="button"
              class="btn btn-outline-secondary"
              @click="cancel"
          >
            <i class="fe fe-x me-1"></i> Annuler
          </button>

          <!-- ENREGISTRER -->
          <button
              v-if="!viewMode"
              class="btn btn-success"
              @click="save"
              :disabled="saving || invalid"
          >
            <i class="fe fe-save me-1"></i>
            {{ submitLabel }}
          </button>

          <!-- MODIFIER -->
          <button
              v-if="viewMode && canEdit"
              class="btn btn-primary"
              @click="goEdit"
          >
            <i class="fe fe-edit me-1"></i> Modifier
          </button>

          <button
              v-if="viewMode && canDelete"
              class="btn btn-outline-danger"
              @click="remove"
          >
            <i class="fe fe-trash me-1"></i> Supprimer
          </button>

        </div>

      </div>

      <div class="card-body">
        <form @submit.prevent="save">

          <div class="panel panel-primary">

            <!-- 🔹 TABS HEADER (STYLE BOXED) -->
            <div class=" tab-menu-heading">
              <div class="tabs-menu1">
                <ul class="nav panel-tabs">
                  <li>
                    <a href="#tab-project" class="active" data-bs-toggle="tab">
                      Projet
                    </a>
                  </li>
                  <li>
                    <a href="#tab-axes" data-bs-toggle="tab">
                      Axes & Actions
                    </a>
                  </li>
                  <li>
                    <a href="#tab-timeline" data-bs-toggle="tab">
                      Timeline
                    </a>
                  </li>
                  <li>
                    <a href="#tab-team" data-bs-toggle="tab">
                      Équipe
                    </a>
                  </li>

                  <li>
                    <a href="#tab-media" data-bs-toggle="tab">
                      Galerie Média
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            <!-- 🔹 TABS CONTENT -->
            <div class="panel-body tabs-menu-body">
              <div class="tab-content">

                <!-- 🟦 TAB 1 : PROJET -->
                <div class="tab-pane active" id="tab-project">
                  <div class="row">

                    <div class="col-md-6">
                      <FieldViewEdit
                          label="Code"
                          v-model="form.code"
                          :rules="editMode ? '' : 'required|min:1|max:3'"
                          :viewMode="viewMode"
                          :disabled="editMode"
                          required
                      />
                    </div>

                    <div class="col-md-6">
                      <FieldViewEdit
                          label="Libellé"
                          v-model="form.label"
                          rules="required|min:3"
                          :viewMode="viewMode"
                      />
                    </div>

                    <div class="col-md-12">
                      <FieldViewEdit
                          label="Description"
                          type="textarea"
                          v-model="form.description"
                          :viewMode="viewMode"
                      />
                    </div>

                  </div>
                </div>

                <!-- 🟩 TAB 2 : AXES & ACTIONS -->
                <div class="tab-pane" id="tab-axes">
                  <ProjectAxesActions
                      v-model="form.axes"
                      :view-mode="viewMode"
                  />
                </div>

                <!-- 🟨 TAB 3 : TIMELINE -->
                <div class="tab-pane" id="tab-timeline">
                  <ProjectTimeline
                      v-model="form.timelines"
                      :view-mode="viewMode"
                  />
                </div>

                <div class="tab-pane" id="tab-team">
                  <ProjectTeamMembers
                      v-model="form.teamMembers"
                      :view-mode="viewMode"
                  />
                </div>


                <!-- 🟪 TAB 4 : GALERIE -->
                <div class="tab-pane" id="tab-media">
                  <ProjectMedia
                      :project-code="form.code"
                      :view-mode="viewMode"
                  />
                </div>

              </div>
            </div>

          </div>


        </form>
      </div>

    </div>
  </ValidationObserver>
</template>


<script>


  import {API} from "../../../../helpers/api-endpoints";
  import {confirmAction} from "../../../../helpers/helpers";
  import FieldViewEdit from "../../../../components/forms/FieldViewEdit.vue";
  import {Operations, Roles} from "../../../../helpers/roles";
  import ProjectAxesActions from "./tabs/ProjectAxesActions.vue";
  import ProjectTimeline from "./tabs/ProjectTimeline.vue";
  import ProjectMedia from "./tabs/ProjectMedia.vue";
  import ProjectTeamMembers from "./tabs/ProjectTeamMembers.vue";

  export default {
    name: 'ProjectForm',
    components: {ProjectTeamMembers, ProjectMedia, ProjectTimeline, ProjectAxesActions, FieldViewEdit},
    props: {
      createMode: { type: Boolean, default: false },
      editMode: { type: Boolean, default: false },
      viewMode: { type: Boolean, default: false },
    },
    data() {
      return {
        saving: false,
        form: {
          code: '',
          label: '',
          description: '',
          timelines: [ ],
          teamMembers: [],
          axes: [],
        },
        operations: [],
        initialForm: null,
      };
    },
    computed: {
      canEditResource() {
        return this.operations.includes(Operations.EDIT);
      },

      canDeleteResource() {
        return this.operations.includes(Operations.DELETE);
      },

      canEdit() {
        return this.canEditResource && this.$can(Roles.PROJET_EDIT);
      },

      canDelete() {
        return this.canDeleteResource && this.$can(Roles.PROJET_DELETE);
      },
      submitLabel() {
        if (this.editMode) return 'Mettre à jour';
        return 'Créer';
      },
      isDirty() {
        return JSON.stringify(this.form) !== this.initialForm;
      },
      title() {
        if (this.viewMode) return 'Détail du projet';
        if (this.editMode) return 'Modifier le projet';
        return 'Créer un projet';
      },
    },
    async mounted() {
      if (this.editMode || this.viewMode) {
        await this.loadProject();
      }
      this.initialForm = JSON.stringify(this.form);
    },
    methods: {
      async remove() {
        const { confirmed } = await confirmAction('delete');
        if (!confirmed) return;

        try {
          this.$loading(true);
          await this.$http.delete(API.PROJECT_DELETE(this.form.code));
          this.$toast.success('Projet supprimé');
          this.$router.push({ name: 'adminProjects' });
        } catch (e) {
          this.$toast.error('Erreur lors de la suppression');
        } finally {
          this.$loading(false);
        }
      },
      async cancel() {
        if (this.isDirty) {
          const { confirmed } = await confirmAction('cancel');
          if (!confirmed) return;
        }

        // 🔄 reset form
        if (this.initialForm) {
          this.form = JSON.parse(this.initialForm);
        }

        // 🧭 retour DIRECT à la liste
        this.$router.push({ name: 'adminProjects' });
      },

      async loadProject() {
        try {
          this.$loading(true);
          const { code } = this.$route.params;

          const { data } = await this.$http.get(
              API.PROJECT_GET(code) // ✅ correction ici
          );

          this.form = {
            code: data.code,
            label: data.label,
            description: data.description,
          };
          this.operations = data.operations || [];
        } catch (e) {
          this.$toast.error('Erreur chargement projet');
        } finally {
          this.$loading(false);
        }
      },
      async save() {
        if (this.viewMode) return;

        // 🔐 confirmation avant action
        const type = this.editMode ? 'edit' : 'create';
        const { confirmed } = await confirmAction(type);
        if (!confirmed) return;

        try {
          this.saving = true;

          if (this.editMode) {
            await this.$http.put(
                API.PROJECT_UPDATE(this.form.code),
                this.form
            );
            this.$toast.success('Projet mis à jour');
          } else {
            await this.$http.post(
                API.PROJECT_CREATE,
                this.form
            );
            this.$toast.success('Projet créé');
          }

          this.$router.push({ name: 'adminProjects' });
        } catch (e) {
          this.$toast.error('Erreur lors de l’enregistrement');
        } finally {
          this.saving = false;
        }
      },

      goEdit() {
        this.$router.push({
          name: 'adminProjectEdit',
          params: { code: this.form.code },
        });
      },
    },
  };
</script>
